import { eventBus } from '../../../core/EventBus';
import { useFileSearchStatusStore } from '../store/useFileSearchStatus';
import { useFileManagerStore } from '../store/useFileManagerStore';
import { WebRTCSystem } from '../api/WebRTCSystem';
import { FileSearchStatus } from '../../../types';

// Map backend status string to frontend enum
const mapBackendStatus = (backendStatus: string): FileSearchStatus => {
  switch (backendStatus) {
    case 'ready':
      return FileSearchStatus.READY;
    case 'initializing':
    case 'indexing':
      return FileSearchStatus.INDEXING;
    case 'building_index':
      return FileSearchStatus.BUILDING_INDEX;
    case 'calculating_sizes':
      return FileSearchStatus.CALCULATING_SIZES;
    case 'error':
    case 'not_started':
    default:
      return FileSearchStatus.UNINITIALIZED;
  }
};

/**
 * Global file search status listener
 * This service starts listening immediately when the module is imported,
 * ensuring status updates are captured even before the Sidebar component mounts.
 */
class FileSearchStatusService {
  private initialized = false;

  initialize() {
    if (this.initialized) return;
    this.initialized = true;

    console.log('[FileSearchStatusService] Initializing global status listener');

    // Listen for status updates from WebRTCService (via DataChannel push)
    eventBus.on('file_search:status', (data: any) => {
      console.log('[FileSearchStatusService] Received status update:', data);

      const mappedStatus = mapBackendStatus(data.status);

      useFileSearchStatusStore.getState().updateStatus({
        status: mappedStatus,
        progress: data.progress,
        current: data.current,
        total: data.total,
        phase: data.phase,
        message: data.message,
      });
    });

    // 监听 store 里的 status 变化:
    // 状态可能通过 event push (updateStatus) 或 主动轮询 (syncFromBackend) 进入 ready,
    // 后者发生在用户强刷页面后. 用 zustand subscribe 统一监听, 任一路径切到 ready
    // 都自动拉 init timing.
    // 同时也处理"前端启动时 backend 已经是 ready 但 initTiming 还没拉"的边界情况.
    useFileSearchStatusStore.subscribe((state, prevState) => {
      const isReadyNow = state.status === FileSearchStatus.READY;
      const wasReady = prevState?.status === FileSearchStatus.READY;
      console.log('[FileSearchStatusService] store subscribe fired',
                  'status:', prevState?.status, '->', state.status,
                  'hasTiming:', !!state.initTiming);
      // status 切到 ready 或当前 ready 但还没 timing → 拉一次
      if (isReadyNow && (!wasReady || !state.initTiming)) {
        if (state.initTiming && wasReady) return;
        console.log('[FileSearchStatusService] triggering fetchInitTiming');
        this.fetchInitTiming();
      }
    });

    // 兜底: service init 后立刻检查 store 当前状态. 防止 store 已是 ready
    // 但 subscribe 注册之前 set 完了 (此时 subscribe 不会回放).
    setTimeout(() => {
      const cur = useFileSearchStatusStore.getState();
      console.log('[FileSearchStatusService] initial check', 'status:', cur.status, 'hasTiming:', !!cur.initTiming);
      if (cur.status === FileSearchStatus.READY && !cur.initTiming) {
        console.log('[FileSearchStatusService] initial: already ready, fetching');
        this.fetchInitTiming();
      }
    }, 0);
  }

  private async fetchInitTiming() {
    const fs = useFileManagerStore.getState().fs;
    console.log('[FileSearchStatusService] fetchInitTiming: fs =', fs,
                'isWebRTC:', fs instanceof WebRTCSystem);
    if (!(fs instanceof WebRTCSystem)) {
      console.warn('[FileSearchStatusService] fetchInitTiming aborted: fs not WebRTCSystem');
      return;
    }
    try {
      const report = await fs.getInitTiming();
      console.log('[FileSearchStatusService] fetchInitTiming response:', report);
      if (report && report.valid) {
        useFileSearchStatusStore.getState().setInitTiming(report);
        console.log('[FileSearchStatusService] init timing fetched:', report);
      } else {
        console.warn('[FileSearchStatusService] fetchInitTiming: report invalid', report);
      }
    } catch (e) {
      console.warn('[FileSearchStatusService] fetchInitTiming failed:', e);
    }
  }
}

export const fileSearchStatusService = new FileSearchStatusService();

// Auto-initialize when module is imported
fileSearchStatusService.initialize();
